<script setup>
import {plainStripeError} from '~/utils/stripeErrors.js';
// The visitor booking sheet (BOOKINGS_PLAN.md B10, BE4, SE20; BookGuest /
// BookGuestDone): no account. Name, email, optional mobile, how long; a card
// through the Payment Element for a paid row. A free booking is held about 15
// minutes until the emailed six-digit code is entered; a paid one is confirmed
// by the payment. The reCAPTCHA v3 token rides along (the API only checks it
// where it is required).
import {clockWord} from '~/utils/bookings';
import {computed, nextTick, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import {useBookingsMember, bookingApiError} from '~/composables/useBookingsMember';
import {useGoogleRecaptcha} from '~/composables/useGoogleRecaptcha';
import {useStripePayments} from '~/composables/useStripePayments';
import {
  cleanCode, dayWord, durationOptions, longDate, looksLikeEmail, moneyExact, openingSlots, runRange, visitorLongestLine, visitorMaxSlots, visitorPriceWords,
} from '~/utils/bookingsMember';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  /** {area, slot, cell, group, column, index} from the grid. */
  pick: {type: Object, default: null},
  /** The public page: clubSlug, clubName, visitorPriceLine, visitorMaxDurationMinutes, visitorAllDay, returnUrl, returnHost, currency. */
  page: {type: Object, default: null},
  date: {type: String, default: null},
  todayDate: {type: String, default: null},
  sportId: {type: String, default: null},
  /** The validated return link {href, host} when there is one. */
  back: {type: Object, default: null},
  /** How many slots the sheet opens at (1 for a click, N for a dragged range), clamped to 1..maxSlots. */
  initialSlots: {type: Number, default: 1},
});
const emit = defineEmits(['update:modelValue', 'booked']);

const api = useBookingsMember();
const {executeRecaptcha} = useGoogleRecaptcha();
const {mountPaymentElement, confirmSetup} = useStripePayments();

const open = computed({get: () => props.modelValue, set: (v) => emit('update:modelValue', v)});
const slotMinutes = computed(() => props.pick?.group?.slotMinutes ?? 60);
// The free run from the cell, capped by the Visitors row's longest booking (the public page carries it).
const maxSlots = computed(() => props.pick ? visitorMaxSlots(props.page, props.pick.column, props.pick.index, slotMinutes.value) : 0);
const options = computed(() => durationOptions(maxSlots.value, slotMinutes.value));
const longestLine = computed(() => visitorLongestLine(props.page));
const slots = ref(1);
const range = computed(() => props.pick ? runRange(props.pick.column, props.pick.index, slots.value, {h24: true}) : '');
const title = computed(() => props.pick ? `${props.pick.area.name} · ${dayWord(props.date, props.todayDate)}, ${clockWord(props.pick.slot.label)}` : '');

const form = ref({name: '', email: '', phone: ''});
const errors = ref({});
const busy = ref(false);
const step = ref('form'); // form | code | pay | done
const result = ref(null);
const code = ref('');

// Paid rows: the Payment Element on the guest customer's SetupIntent (platform).
const elementHost = ref(null);
const elementReady = ref(false);
const stripeError = ref(null);
let session = null;

const reset = () => {
  slots.value = openingSlots(props.initialSlots, maxSlots.value);
  form.value = {name: '', email: '', phone: ''};
  errors.value = {};
  step.value = 'form';
  result.value = null;
  code.value = '';
  elementReady.value = false;
  stripeError.value = null;
  session = null;
};
watch(() => [props.modelValue, props.pick], ([isOpen]) => { if (isOpen) reset(); }, {immediate: true});

const validate = () => {
  const e = {};
  if (!form.value.name.trim()) e.name = ['Your name'];
  if (!looksLikeEmail(form.value.email)) e.email = ['A real email address: your booking and the cancel link go there'];
  errors.value = e;
  return !Object.keys(e).length;
};

const mountCard = async () => {
  if (!result.value?.setupIntentClientSecret) return;
  await nextTick();
  if (!elementHost.value) return;
  try {
    session = await mountPaymentElement(result.value.setupIntentClientSecret, elementHost.value, {savingCard: false});
    session.paymentElement.on('ready', () => { elementReady.value = true; });
  } catch (e) {
    stripeError.value = e?.message || 'Could not start the card form. Please try again.';
  }
};

const apply = async (res) => {
  result.value = res;
  switch (res?.outcome) {
    case 'held': step.value = 'code'; break;
    case 'pendingPayment': step.value = 'pay'; await mountCard(); break;
    case 'requiresAction': {
      const {error} = await api.finishCardAction({clientSecret: res.clientSecret, stripeAccountId: res.stripeAccountId});
      if (error) { errors.value = {global: [plainStripeError(error)]}; step.value = 'pay'; await mountCard(); return; }
      return apply(await api.visitorPaySync(res.token));
    }
    case 'failed': errors.value = {global: [res.message || "Your card didn't go through. Try another card."]}; step.value = 'pay'; await mountCard(); break;
    case 'confirmed': step.value = 'done'; emit('booked', res); break;
    default: errors.value = {global: [res?.message || 'Something went wrong. Please try again']};
  }
};

const start = async () => {
  if (!validate()) return;
  busy.value = true;
  try {
    const {token} = await executeRecaptcha('visitor_booking');
    await apply(await api.visitorStart(props.page.clubSlug, {
      playingAreaId: props.pick.area.id,
      startsAt: props.pick.slot.startsAt,
      slots: slots.value,
      sportId: props.sportId || null,
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      phone: form.value.phone.trim() || null,
      recaptchaToken: token,
      returnUrl: props.page?.returnUrl || null,
    }));
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "That couldn't be booked.")]};
  } finally {
    busy.value = false;
  }
};
const confirmCode = async () => {
  if (cleanCode(code.value).length !== 6) { errors.value = {code: ['The six digits from the email']}; return; }
  busy.value = true;
  errors.value = {};
  try {
    await apply(await api.visitorConfirm(result.value.token, cleanCode(code.value)));
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "That code didn't match.")]};
  } finally {
    busy.value = false;
  }
};
const pay = async () => {
  if (!session) return;
  busy.value = true;
  errors.value = {};
  stripeError.value = null;
  try {
    const {error, setupIntent} = await confirmSetup(session);
    if (error) { stripeError.value = plainStripeError(error); return; }
    const pm = typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : setupIntent?.payment_method?.id;
    await apply(await api.visitorPay(result.value.token, pm));
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "The payment couldn't be taken.")]};
  } finally {
    busy.value = false;
  }
};
const firstName = computed(() => String(form.value.name).trim().split(/\s+/)[0] || '');
// X74 (R25 L1): the cell's own group price times the slots picked, not the club's cheapest visitor line.
const price = computed(() => visitorPriceWords(props.page, props.pick?.group?.id ?? null, slots.value));
const priceWord = computed(() => price.value.line);
const priceTail = computed(() => (price.value.amountMinor > 0 ? ` · ${moneyExact(price.value.amountMinor, props.page?.currency)}` : ''));
const paidLine = computed(() => result.value?.priceMinor > 0 ? ` · paid ${moneyExact(result.value.priceMinor, result.value.currency)}` : '');
const registerTo = computed(() => ({path: '/register', query: {redirectUrl: `/clubs/${props.page?.clubSlug}/book`}}));
</script>

<template>
  <tl-dialog v-model="open" :title="step === 'done' ? 'Booked' : title" :max-width="460" :persistent="step === 'code' || step === 'pay'">
    <template v-if="pick">
      <!-- ── Done (BookGuestDone) ── -->
      <div v-if="step === 'done'" class="vb-done">
        <v-icon size="42" color="success" icon="mdi-check-circle"/>
        <div class="vb-done-title">You're booked<template v-if="firstName">, {{ firstName }}</template></div>
        <div class="vb-sub">{{ result?.line || (pick.area.name + ' · ' + longDate(date) + ', ' + range) }}{{ paidLine }}</div>
        <div class="vb-sub mt-2">{{ result?.message || `We've emailed ${form.email.trim()} with the details and a link to cancel.` }}</div>
        <v-btn v-if="back" :href="back.href" color="primary" rounded="pill" class="mt-4" prepend-icon="mdi-arrow-left">Back to {{ back.host }}</v-btn>
        <div class="vb-account">
          <div class="vb-account-title">Play here often?</div>
          <div class="vb-sub">Create a free account to book faster next time.</div>
          <v-btn variant="tonal" color="primary" rounded="pill" size="small" class="mt-2" :to="registerTo">Create an account</v-btn>
        </div>
      </div>

      <!-- ── Code (BE4: a free booking is held until the emailed code) ── -->
      <div v-else-if="step === 'code'">
        <div class="vb-sub">{{ result?.line }}</div>
        <p class="vb-note">{{ result?.message || `We've emailed a six-digit code to ${form.email.trim()}. Enter it to confirm; the slot is held for about 15 minutes.` }}</p>
        <div class="tl-label mb-1 mt-2">Code from the email</div>
        <v-text-field v-model="code" variant="outlined" density="compact" hide-details="auto" inputmode="numeric" autocomplete="one-time-code"
                      placeholder="123456" class="vb-code" :error-messages="errors.code" @update:model-value="code = cleanCode($event)" @keydown.enter.prevent="confirmCode"/>
        <crud-error-display class="mt-3" :errors="errors"/>
      </div>

      <!-- ── Pay (a paid Visitors row) ── -->
      <div v-else-if="step === 'pay'">
        <div class="vb-rows">
          <div class="vb-row"><span class="k">Booking</span><span class="v">{{ result?.line }}</span></div>
          <div class="vb-row"><span class="k">Visitor price</span><span class="v vb-amt">{{ moneyExact(result?.priceMinor ?? 0, result?.currency) }}</span></div>
        </div>
        <p class="vb-note">{{ result?.message || 'Pay to confirm. The slot is held for about 15 minutes.' }}</p>
        <div class="tl-label mb-1 mt-2">Card</div>
        <div v-if="!elementReady && !stripeError" class="vb-loading"><v-progress-circular indeterminate size="20" width="2" color="primary"/> Preparing secure card form…</div>
        <div ref="elementHost"/>
        <v-alert v-if="stripeError" type="error" variant="tonal" density="compact" class="mt-3">{{ stripeError }}</v-alert>
        <p class="vb-note">{{ result?.cancelLine || 'Cancel before the cut-off for a full refund.' }} Tap Leagues and the club never see your card number.</p>
        <crud-error-display class="mt-3" :errors="errors"/>
      </div>

      <!-- ── Form (BookGuest) ── -->
      <div v-else>
        <div class="vb-sub">{{ longDate(date) }}</div>
        <div class="vb-signin">Book as a visitor. Have a Tap Leagues account? <nuxt-link :to="{path: '/login', query: {redirectUrl: $route.fullPath}}">Sign in</nuxt-link></div>

        <div class="tl-label mt-3 mb-1">How long</div>
        <div class="vb-pills">
          <button v-for="o in options" :key="o.slots" type="button" class="vb-pill" :class="{'vb-pill--on': slots === o.slots}" @click="slots = o.slots">{{ o.label }}</button>
        </div>
        <div v-if="longestLine" class="vb-hint">{{ longestLine }}</div>

        <div class="tl-label mt-4 mb-1">Your name</div>
        <v-text-field v-model="form.name" variant="outlined" density="compact" hide-details="auto" placeholder="Tom Hughes" autocomplete="name" :error-messages="errors.name"/>
        <div class="tl-label mt-3 mb-1">Email</div>
        <v-text-field v-model="form.email" type="email" variant="outlined" density="compact" hide-details="auto" placeholder="tom.hughes@example.com" autocomplete="email" :error-messages="errors.email"/>
        <div class="vb-hint">We'll email your booking and a link to cancel.</div>
        <div class="tl-label mt-3 mb-1">Mobile <span class="vb-opt">(optional)</span></div>
        <v-text-field v-model="form.phone" type="tel" variant="outlined" density="compact" hide-details placeholder="07700 900123" autocomplete="tel"/>

        <div class="vb-rows mt-4">
          <div class="vb-row"><span class="k">Time</span><span class="v">{{ range }}</span></div>
          <div class="vb-row"><span class="k">Visitor price</span><span class="v vb-amt">{{ priceWord }}</span></div>
        </div>
        <p class="vb-note">A visitor can have one booking at a time. Paid bookings are refunded in full if cancelled before the cut-off.</p>
        <crud-error-display class="mt-3" :errors="errors"/>
      </div>
    </template>

    <template #actions>
      <template v-if="step === 'done'">
        <v-btn color="primary" @click="open = false">Done</v-btn>
      </template>
      <template v-else-if="step === 'code'">
        <v-btn variant="outlined" :disabled="busy" @click="open = false">Later</v-btn>
        <v-btn color="primary" :loading="busy" :disabled="cleanCode(code).length !== 6" @click="confirmCode">Confirm booking</v-btn>
      </template>
      <template v-else-if="step === 'pay'">
        <v-btn variant="outlined" :disabled="busy" @click="open = false">Later</v-btn>
        <v-btn color="primary" prepend-icon="mdi-lock" :loading="busy" :disabled="!elementReady" @click="pay">Pay {{ moneyExact(result?.priceMinor ?? 0, result?.currency) }} and book</v-btn>
      </template>
      <template v-else>
        <v-btn variant="outlined" :disabled="busy" @click="open = false">Cancel</v-btn>
        <v-btn color="primary" :loading="busy" @click="start">Book {{ pick?.area.name }}, {{ range }}{{ priceTail }}</v-btn>
      </template>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.vb-sub { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; }
.vb-signin { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 6px; a { color: rgb(var(--v-theme-primary)); font-weight: 600; } }
.vb-pills { display: flex; flex-wrap: wrap; gap: 8px; }
.vb-pill {
  height: 34px; padding: 0 14px; border-radius: 999px; border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface)); font-size: 12.5px; font-weight: 600; font-family: inherit; cursor: pointer;
  &--on { border-color: rgb(var(--v-theme-primary)); background: rgb(var(--v-theme-brandTint)); color: rgb(var(--v-theme-primary)); }
}
.vb-opt { font-weight: 500; text-transform: none; letter-spacing: 0; color: rgb(var(--v-theme-textSubtle)); }
.vb-hint { font-size: 11.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 4px; }
.vb-rows { border: 1px solid rgb(var(--v-theme-borderLight)); border-radius: 12px; overflow: hidden; background: rgb(var(--v-theme-light)); }
.vb-row {
  display: flex; gap: 12px; align-items: center; padding: 9px 14px; font-size: 13px; border-top: 1px solid rgb(var(--v-theme-borderLight));
  &:first-child { border-top: 0; }
  .k { width: 100px; flex-shrink: 0; font-size: 12px; font-weight: 600; color: rgb(var(--v-theme-textSubtle)); }
  .v { flex: 1; line-height: 1.45; }
}
.vb-amt { font-weight: 800; font-size: 15px; }
.vb-note { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; margin: 12px 2px 0; }
.vb-loading { display: flex; align-items: center; gap: 10px; font-size: 13px; color: rgb(var(--v-theme-textSubtle)); padding: 10px 0; }
.vb-code :deep(input) { font-size: 22px; letter-spacing: 8px; font-weight: 700; text-align: center; }
.vb-done { text-align: center; padding: 8px 4px 0; }
.vb-done-title { font-size: 18px; font-weight: 800; letter-spacing: -0.3px; margin-top: 6px; }
.vb-account { margin-top: 20px; padding: 14px; border-radius: 12px; border: 1px solid rgb(var(--v-theme-borderLight)); text-align: left; }
.vb-account-title { font-size: 14px; font-weight: 700; }
</style>
